import { useEffect, useState } from 'react';
import { api, downloadFile, errorText } from '../api';
import { useAuth } from '../auth';
import { Card, Notice } from '../components/ui';
import type { Warehouse, Zone } from '../types';

/**
 * Etikettenbögen. A4-Raster statt Rollendrucker: für die Ersteinrichtung
 * eines Lagers ist genau das der Weg, und es braucht keinen Spezialdrucker.
 */
export function LabelsPage() {
  const { owners } = useAuth();
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [ownerId, setOwnerId] = useState('');

  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<Warehouse[]>('/api/warehouse').then(setWarehouses).catch(() => setWarehouses([]));
  }, []);

  useEffect(() => {
    api<Zone[]>('/api/warehouse/zones', { query: { warehouseId } })
      .then(setZones)
      .catch(() => setZones([]));
    setZoneId('');
  }, [warehouseId]);

  async function print(path: string, body: unknown, what: string) {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await downloadFile(path, { method: 'POST', body });
      setMessage(`${what} wurden erzeugt.`);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}
      {message && <Notice kind="success">{message}</Notice>}

      <Notice kind="info">
        Die Bögen enthalten Code128-Barcodes. Auf Artikeletiketten steht die EAN, sofern gepflegt —
        danach greift der Scanner am Wareneingang ohnehin zuerst.
      </Notice>

      <div className="grid two">
        <Card title="Lagerplatz-Etiketten">
          <p className="small muted" style={{ marginTop: 0 }}>
            Ein Etikett je Regalplatz, sortiert nach Lager und Platzcode. Virtuelle
            Gegenbuchungsplätze bleiben aussen vor.
          </p>
          <div className="form-grid">
            <label className="field">
              <span>Lager</span>
              <select value={warehouseId} onChange={(e) => setWarehouseId(e.target.value)}>
                <option value="">alle Lager</option>
                {warehouses.map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.code} · {warehouse.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Zone (optional)</span>
              <select
                value={zoneId}
                onChange={(e) => setZoneId(e.target.value)}
                disabled={zones.length === 0}
              >
                <option value="">alle Zonen</option>
                {zones.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.code} · {zone.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button
            className="primary"
            disabled={busy}
            onClick={() =>
              print(
                '/api/printing/labels/locations',
                { warehouseId: warehouseId || undefined, zoneId: zoneId || undefined },
                'Lagerplatz-Etiketten',
              )
            }
          >
            {busy ? 'Wird erzeugt …' : 'Etikettenbogen erzeugen'}
          </button>
        </Card>

        <Card title="Artikeletiketten">
          <p className="small muted" style={{ marginTop: 0 }}>
            Ein Etikett je aktivem Artikel mit Bezeichnung, Artikelnummer und Eigentümer.
          </p>
          <div className="form-grid">
            <label className="field">
              <span>Eigentümer</span>
              <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                <option value="">alle Eigentümer</option>
                {owners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <button
            className="primary"
            disabled={busy}
            onClick={() =>
              print(
                '/api/printing/labels/articles',
                { ownerId: ownerId || undefined },
                'Artikeletiketten',
              )
            }
          >
            {busy ? 'Wird erzeugt …' : 'Etikettenbogen erzeugen'}
          </button>
        </Card>
      </div>
    </>
  );
}
